<template>
    <div class="AiIntegrationsComponent p-4">
        <div class="flex">
            <div class="relative mr-2 w-1">
                <div class="absolute inset-y-0 left-0 w-full bg-theme-accent"></div>
            </div>
            <div class="flex-1">
                <div class="mb-3">
                    <div
                        class="inline-flex items-center gap-2 rounded-md border border-theme-badge-border bg-theme-badge-bg px-3 py-1"
                    >
                        <div class="h-2 w-2 rounded-full bg-theme-badge-dot"></div>
                        <span class="text-sm font-medium text-theme-badge-text">{{ integrationTitle }}</span>
                    </div>
                    <div v-if="isPdf" class="mt-1 text-xs text-theme-toolbar-icon">
                        {{ localizations[lang].page }} {{ ui.selectionTool.page }}
                    </div>
                </div>
                <div ref="selectedText" class="font-noto-sans mb-4 text-left font-normal leading-6 text-theme-text">
                    {{ currentHighlight.totalText }}
                </div>
                <div class="font-noto-sans mt-4 text-left text-sm font-normal leading-6">
                    <div
                        v-if="loading"
                        role="status"
                        aria-live="polite"
                        class="flex flex-col items-center justify-center py-8"
                    >
                        <SimpleSpinner width="32" height="32" />
                        <p class="mt-3 text-sm text-theme-toolbar-icon">
                            {{ translations.integrations['ai-integrations'].loading[lang].generating }}...
                        </p>
                    </div>
                    <div v-else-if="hasError" role="alert" aria-live="assertive" class="py-8 text-center">
                        <div class="mb-4">
                            <svg
                                class="mx-auto h-12 w-12 text-theme-toolbar-icon"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="1.5"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                ></path>
                            </svg>
                        </div>
                        <div class="mx-auto mb-6 max-w-sm text-theme-text">
                            {{ actionResponse }}
                        </div>
                        <div class="flex justify-center">
                            <BaseButton
                                unstyled
                                :disabled="loading"
                                class="rounded-lg bg-theme-accent px-6 py-2 text-sm font-medium text-theme-accent-on shadow-sm transition-opacity duration-200 hover:opacity-90 hover:shadow-md"
                                @click="handleTextChange"
                            >
                                {{ translations.integrations['ai-integrations'].retry[lang] }}
                            </BaseButton>
                        </div>
                    </div>
                    <div v-else :lang="issue.lang" class="text-theme-text">
                        {{ actionResponse }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Action buttons -->
        <div v-if="!loading && !hasError" class="mt-4 flex items-center justify-start space-x-2">
            <BaseButton
                :disabled="feedbackSent === 'good'"
                :tooltip="localizations[lang].tooltips.goodResponse"
                :icon="feedbackSent === 'good' ? 'thumb-up' : 'thumb-up-outline'"
                class="cursor-pointer border-none bg-transparent p-1 text-theme-toolbar-icon hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-50"
                @click="sendFeedback(1)"
            />
            <BaseButton
                :disabled="feedbackSent === 'bad'"
                :tooltip="localizations[lang].tooltips.badResponse"
                :icon="feedbackSent === 'bad' ? 'thumb-down' : 'thumb-down-outline'"
                class="cursor-pointer border-none bg-transparent p-1 text-theme-toolbar-icon hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-50"
                @click="sendFeedback(0)"
            />
            <BaseButton
                :tooltip="localizations[lang].tooltips.copy"
                icon="copy"
                class="cursor-pointer border-none bg-transparent p-1 text-theme-toolbar-icon hover:bg-transparent"
                @click="copyResponseToClipboard"
            />
            <BaseButton
                :tooltip="localizations[lang].listening.label"
                icon="speaker-24px"
                class="cursor-pointer border-none bg-transparent p-1 text-theme-toolbar-icon hover:bg-transparent"
                @click="listeningHighlight"
            />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import BaseButton from '../../Components/buttons/BaseButton.vue';
import SimpleSpinner from '../../Utilities/SimpleSpinner.vue';
import ContentEncryptionService from '../../../../src/js/components/lib/ContentEncryptionService.js';
import Decrypter from '../../../../src/js/components/lib/Decrypter.js';
import Encrypter from '../../../../src/js/components/lib/Encrypter.js';
import { aiIntegrationsAPI } from '../../../../src/js/communication/integrations';

export default {
    name: 'AiIntegrations',
    components: {
        BaseButton,
        SimpleSpinner,
    },
    props: {
        text: {
            type: String,
            required: true,
        },
        integrationTitle: {
            type: String,
            required: true,
        },
    },
    emits: ['call-close'],
    data() {
        return {
            loading: true,
            integrationSlug: 'ai-integrations',
            actionResponse: '',
            uuid: '',
            feedbackSent: '',
            isError: false,
        };
    },
    computed: {
        ...mapState([
            'ui',
            'integrations',
            'translations',
            'lang',
            'localizations',
            'issue',
            'integrationTool',
            'currentHighlight',
        ]),
        ...mapGetters(['isEpub', 'isPdf']),
        textToSpeechOrTranslate() {
            return this.actionResponse;
        },
        hasError() {
            return this.isError;
        },
    },
    watch: {
        text: 'handleTextChange',
    },
    mounted() {
        this.handleTextChange();
    },
    methods: {
        ...mapActions(['trackEvent', 'copyToClipBoard', 'listeningHighlights']),
        ...mapMutations(['openToast']),
        async handleTextChange() {
            this.loading = true;
            this.actionResponse = '';
            this.isError = false;

            try {
                const response = await aiIntegrationsAPI.generateResponse({
                    action: this.integrationTool.action,
                    text: ContentEncryptionService.shouldEncrypt() ? await Encrypter.encryptText(this.text) : this.text,
                    lang: this.issue.lang,
                });

                // Validate response structure
                if (!response) {
                    throw new Error('Invalid response structure');
                }

                // Check if data array exists and has content
                if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                    throw new Error('Empty response data');
                }

                // Check if the first element exists and is not empty
                const responseData = response.data[0];
                if (!responseData || (typeof responseData === 'string' && responseData.trim() === '')) {
                    throw new Error('Empty response content');
                }

                // Process the response successfully
                this.actionResponse = ContentEncryptionService.shouldEncrypt()
                    ? await Decrypter.decryptText(responseData)
                    : responseData;
                this.uuid = response.uuid;
                this.isError = false;
            } catch (e) {
                console.error('Error in AI integration:', e);
                this.isError = true;

                // Provide specific error messages based on error type
                if (e.message === 'Empty response data' || e.message === 'Empty response content') {
                    this.actionResponse =
                        this.translations.integrations['ai-integrations'].errors['empty-response'].message[this.lang];
                } else if (e.message === 'Invalid response structure') {
                    this.actionResponse =
                        this.translations.integrations['ai-integrations'].errors['invalid-response'].message[this.lang];
                } else {
                    // Use the configured error message for other errors
                    this.actionResponse =
                        this.translations.integrations['ai-integrations'].errors['no-results'].message[this.lang];
                }
            } finally {
                this.loading = false;
            }
        },
        saveResponse() {
            this.openToast({ text: this.localizations[this.lang]['ai-integrations'].responseSaved });
            this.trackEvent({
                action: 'AI Integrations - Save Response',
                trigger: 'click',
                page: '',
                source: 'ai integrations modal',
            });
            this.$emit('call-close');
        },
        async copyResponseToClipboard() {
            const textToCopy = `${this.actionResponse.trim()}. ${this.localizations[this.lang]['ai-integrations'].messages.addedToResponse}`;
            const wasCopied = await this.copyToClipBoard(textToCopy);
            if (!wasCopied) return;

            this.openToast({ text: this.localizations[this.lang]['ai-integrations'].messages.copied });
        },
        async sendFeedback(score) {
            try {
                this.feedbackSent = score ? 'good' : 'bad';
                await aiIntegrationsAPI.sendFeedback({
                    uuid: this.uuid,
                    score,
                });
                this.openToast({ text: this.localizations[this.lang]['ai-integrations'].thanksFeedback });
            } catch (_error) {
                this.feedbackSent = '';
                this.openToast({ text: this.localizations[this.lang]['ai-integrations'].errorFeedback });
            }
        },
        listeningHighlight() {
            this.listeningHighlights({
                text: this.textToSpeechOrTranslate,
                uuidToListen: this.uuid,
            });
            this.trackEvent({
                action: 'AI Integrations - Listening',
                trigger: 'click',
                page: '',
                source: 'ai integrations modal',
            });
        },
    },
};
</script>

<style scoped lang="scss">
.AiIntegrationsComponent {
    padding: 1rem;
}

.action-button {
    &:disabled {
        @apply cursor-not-allowed opacity-50;
    }
}
</style>
